/**
 * My Products API — typed wrappers around `/api/householdproducts/*`.
 *
 * Ported from legacy `frontend/src/services/householdProductsService.ts`.
 */

import { z } from 'zod';
import type { HttpClient } from './client';
import {
  type CreateHouseholdProductRequest,
  type LinkOfferToHouseholdProductRequest,
  type UpdateHouseholdProductRequest,
} from '../types/requests';
import {
  type BrandPreference,
  type BrandsResponse,
  type HouseholdProduct_RetailerOffer_LinkWithDetails,
  type HouseholdProductOffersResponse,
  type HouseholdProductPreferences,
  type HouseholdProductWithDetails,
  type HouseholdProductsResponse,
  brandsResponseSchema,
  householdProductPreferencesSchema,
  myProductOfferLinkWithDetailsSchema,
  myProductOffersResponseSchema,
  myProductWithDetailsSchema,
  householdProductsResponseSchema,
  type MeasurementChoicesResponse,
  measurementChoicesResponseSchema,
} from '../types/HouseholdProduct';
import {
  type SystemProductPreference,
  type SystemProductSearchParams,
  type SystemProductSearchResponse,
  type SystemProductOffersResponse,
  systemProductSearchResponseSchema,
  systemProductOffersResponseSchema,
} from '../types/SystemProduct';
import {
  type SuggestionsResponse,
  suggestionsResponseSchema,
} from '../types/suggestions';

const successSchema = z.object({ success: z.boolean() });

const API_BASE = '/api/householdproducts';

export class HouseholdProductsApi {
  constructor(private readonly http: HttpClient) {}

  fetchAll(): Promise<HouseholdProductsResponse> {
    return this.http.get(API_BASE, householdProductsResponseSchema);
  }

  search(query: string): Promise<HouseholdProductsResponse> {
    return this.http.get(API_BASE, householdProductsResponseSchema, { search: query });
  }

  fetch(productId: string): Promise<HouseholdProductWithDetails> {
    return this.http.get(
      `${API_BASE}/${encodeURIComponent(productId)}`,
      myProductWithDetailsSchema
    );
  }

  create(input: CreateHouseholdProductRequest): Promise<HouseholdProductWithDetails> {
    return this.http.post(API_BASE, input, myProductWithDetailsSchema);
  }

  update(
    productId: string,
    input: UpdateHouseholdProductRequest
  ): Promise<HouseholdProductWithDetails> {
    return this.http.patch(
      `${API_BASE}/${encodeURIComponent(productId)}`,
      input,
      myProductWithDetailsSchema
    );
  }

  delete(productId: string): Promise<void> {
    return this.http.delete(`${API_BASE}/${encodeURIComponent(productId)}`);
  }

  fetchLinkedOffers(productId: string): Promise<HouseholdProductOffersResponse> {
    return this.http.get(
      `${API_BASE}/${encodeURIComponent(productId)}/offers`,
      myProductOffersResponseSchema
    );
  }

  linkOffer(
    productId: string,
    input: LinkOfferToHouseholdProductRequest
  ): Promise<HouseholdProduct_RetailerOffer_LinkWithDetails> {
    return this.http.post(
      `${API_BASE}/${encodeURIComponent(productId)}/link-offer`,
      input,
      myProductOfferLinkWithDetailsSchema
    );
  }

  updateOfferLink(
    productId: string,
    offerId: string,
    input: Partial<Omit<LinkOfferToHouseholdProductRequest, 'retailerofferId'>>
  ): Promise<HouseholdProduct_RetailerOffer_LinkWithDetails> {
    return this.http.patch(
      `${API_BASE}/${encodeURIComponent(productId)}/link-offer/${encodeURIComponent(offerId)}`,
      input,
      myProductOfferLinkWithDetailsSchema
    );
  }

  unlinkOffer(productId: string, offerId: string): Promise<void> {
    return this.http.delete(
      `${API_BASE}/${encodeURIComponent(productId)}/link-offer/${encodeURIComponent(offerId)}`
    );
  }

  // -------------------------------------------------------------------------
  // T12.1.7 — preferences surface (brands / retailers / store-locations) +
  // the managed Brand catalog the brand picker reads from. All soft signals.
  // Folded onto this API (rather than a new bundle entry) so the ApiBundle
  // shape stays stable; the brand catalog read targets `/api/brands`.
  // -------------------------------------------------------------------------

  fetchPreferences(productId: string): Promise<HouseholdProductPreferences> {
    return this.http.get(
      `${API_BASE}/${encodeURIComponent(productId)}/preferences`,
      householdProductPreferencesSchema
    );
  }

  fetchBrands(): Promise<BrandsResponse> {
    return this.http.get('/api/brands', brandsResponseSchema);
  }

  linkBrandPreference(
    productId: string,
    brandId: string,
    preference: BrandPreference
  ): Promise<void> {
    return this.http
      .post(
        `${API_BASE}/${encodeURIComponent(productId)}/preferences/brands`,
        { brandId, preference },
        successSchema
      )
      .then(() => undefined);
  }

  setBrandPreference(
    productId: string,
    brandId: string,
    preference: BrandPreference
  ): Promise<void> {
    return this.http
      .patch(
        `${API_BASE}/${encodeURIComponent(productId)}/preferences/brands/${encodeURIComponent(brandId)}/preference`,
        { preference },
        successSchema
      )
      .then(() => undefined);
  }

  unlinkBrandPreference(productId: string, brandId: string): Promise<void> {
    return this.http.delete(
      `${API_BASE}/${encodeURIComponent(productId)}/preferences/brands/${encodeURIComponent(brandId)}`
    );
  }

  linkRetailerPreference(productId: string, retailerId: string): Promise<void> {
    return this.http
      .post(
        `${API_BASE}/${encodeURIComponent(productId)}/preferences/retailers`,
        { retailerId },
        successSchema
      )
      .then(() => undefined);
  }

  unlinkRetailerPreference(productId: string, retailerId: string): Promise<void> {
    return this.http.delete(
      `${API_BASE}/${encodeURIComponent(productId)}/preferences/retailers/${encodeURIComponent(retailerId)}`
    );
  }

  linkStoreLocationPreference(productId: string, storeLocationId: string): Promise<void> {
    return this.http
      .post(
        `${API_BASE}/${encodeURIComponent(productId)}/preferences/store-locations`,
        { storeLocationId },
        successSchema
      )
      .then(() => undefined);
  }

  unlinkStoreLocationPreference(productId: string, storeLocationId: string): Promise<void> {
    return this.http.delete(
      `${API_BASE}/${encodeURIComponent(productId)}/preferences/store-locations/${encodeURIComponent(storeLocationId)}`
    );
  }

  /**
   * Set/clear the cadence nudge. Routes through PATCH /:id (the
   * householdproduct-update path) so the change emits the product_updated
   * sync event. `null` clears to algorithm-derived.
   */
  setCadenceDays(productId: string, cadenceDays: number | null): Promise<HouseholdProductWithDetails> {
    return this.http.patch(
      `${API_BASE}/${encodeURIComponent(productId)}`,
      { cadenceDays },
      myProductWithDetailsSchema
    );
  }

  // -------------------------------------------------------------------------
  // T12.1.8.2 — the SystemProduct surface: the search-first picker read, the
  // "who offers it" read, and the SystemProduct connection (the foreground
  // product-preference control). Folded onto this API to keep the ApiBundle
  // shape stable. Search + offers target `/api/systemproducts`; the connection
  // write targets the householdproduct preference sub-resource.
  // -------------------------------------------------------------------------

  searchSystemProducts(params: SystemProductSearchParams): Promise<SystemProductSearchResponse> {
    const queryParams: Record<string, string> = {};
    if (params.query) queryParams.query = params.query;
    if (params.category_ids && params.category_ids.length > 0) {
      queryParams.category_ids = params.category_ids.join(',');
    }
    if (params.brand_id) queryParams.brand_id = params.brand_id;
    if (params.available_only) queryParams.available_only = 'true';
    if (params.limit !== undefined) queryParams.limit = String(params.limit);
    if (params.offset !== undefined) queryParams.offset = String(params.offset);

    return this.http.get(
      '/api/systemproducts/search',
      systemProductSearchResponseSchema,
      queryParams
    );
  }

  listSystemProductOffers(systemProductId: string): Promise<SystemProductOffersResponse> {
    return this.http.get(
      `/api/systemproducts/${encodeURIComponent(systemProductId)}/offers`,
      systemProductOffersResponseSchema
    );
  }

  linkSystemProductPreference(
    productId: string,
    systemProductId: string,
    preference: SystemProductPreference
  ): Promise<void> {
    return this.http
      .post(
        `${API_BASE}/${encodeURIComponent(productId)}/preferences/system-products`,
        { systemProductId, preference },
        successSchema
      )
      .then(() => undefined);
  }

  setSystemProductPreference(
    productId: string,
    systemProductId: string,
    preference: SystemProductPreference
  ): Promise<void> {
    return this.http
      .patch(
        `${API_BASE}/${encodeURIComponent(productId)}/preferences/system-products/${encodeURIComponent(systemProductId)}/preference`,
        { preference },
        successSchema
      )
      .then(() => undefined);
  }

  unlinkSystemProductPreference(productId: string, systemProductId: string): Promise<void> {
    return this.http.delete(
      `${API_BASE}/${encodeURIComponent(productId)}/preferences/system-products/${encodeURIComponent(systemProductId)}`
    );
  }

  // -------------------------------------------------------------------------
  // T36.3 — measurement settings picker. List the valid choices, save a `user`
  // override (a chosen packing), or "use suggested" (clear back to `system`).
  // -------------------------------------------------------------------------

  listMeasurementChoices(productId: string): Promise<MeasurementChoicesResponse> {
    return this.http.get(
      `${API_BASE}/${encodeURIComponent(productId)}/measurement-choices`,
      measurementChoicesResponseSchema
    );
  }

  setMeasurement(productId: string, packingId: string): Promise<HouseholdProductWithDetails> {
    return this.http.put(
      `${API_BASE}/${encodeURIComponent(productId)}/measurement`,
      { packingId },
      myProductWithDetailsSchema
    );
  }

  // The per-product "Show in" display-unit switch (ADR-015 §5-6). Keeps the
  // current packing; re-points the displayed unit to a stored form.
  setDisplayUnit(productId: string, unit: string): Promise<HouseholdProductWithDetails> {
    return this.http.put(
      `${API_BASE}/${encodeURIComponent(productId)}/measurement/display-unit`,
      { unit },
      myProductWithDetailsSchema
    );
  }

  useSuggestedMeasurement(productId: string): Promise<HouseholdProductWithDetails> {
    // DELETE with a parsed body response — the http client's delete returns void,
    // so we re-fetch is unnecessary: the route returns the updated product, but
    // delete() discards it. Use a typed delete if available; else re-fetch.
    return this.http
      .delete(`${API_BASE}/${encodeURIComponent(productId)}/measurement`)
      .then(() => this.fetch(productId));
  }

  // -------------------------------------------------------------------------
  // T12.5 — the "Suggested · likely needed soon" shortlist read for the
  // Add-to-list sheet. Targets `/api/suggestions/products`; folded onto this
  // API (rather than a new ApiBundle entry) so the bundle shape stays stable.
  // Display + tap-to-add only — how the list is ranked is the backend's call.
  // `excludeHouseholdProductIds` (the active list's items) is forwarded
  // comma-joined so the server drops them; `limit` defaults to 10.
  // -------------------------------------------------------------------------

  listSuggestions(
    options: { excludeHouseholdProductIds?: string[]; limit?: number } = {}
  ): Promise<SuggestionsResponse> {
    const { excludeHouseholdProductIds = [], limit = 10 } = options;
    const queryParams: Record<string, string> = { limit: String(limit) };
    if (excludeHouseholdProductIds.length > 0) {
      queryParams.exclude_householdproduct_ids = excludeHouseholdProductIds.join(',');
    }
    return this.http.get(
      '/api/suggestions/products',
      suggestionsResponseSchema,
      queryParams
    );
  }
}
